import { useEffect, useRef } from 'react';
import Konva from 'konva';
import { useSelectedElementId, useTemplateStore, useVisibleSortedElements } from './useTemplateStore';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Permission } from '@/lib/api/interfaces/permissions.interface';

/**
 * Hook for managing the Konva Transformer
 */
export const useTransformer = (stage: Konva.Stage | null) => {
  const transformerRef = useRef<Konva.Transformer>(null);
  const selectedElementId = useSelectedElementId();
  const elements = useVisibleSortedElements();
  const selectElement = useTemplateStore(state => state.selectElement);
  const { can } = usePermissions();

  /**
   * Connect transformer to selected element
   */
  useEffect(() => {
    const transformer = transformerRef.current;

    if (!transformer || !stage) return;

    const updateTransformer = () => {
      const selectedElement = elements.find(el => el.id === selectedElementId);
      const targetId =
        selectedElement && selectedElement.type === 'image'
          ? `${selectedElementId}-transform-target`
          : selectedElementId;

      const selectedNode = targetId ? stage.findOne(`#${targetId}`) : null;

      if (selectedElementId && (!selectedElement || !selectedElement.visible)) {
        selectElement(null);
        transformer.nodes([]);
      } else if (selectedNode && selectedElement) {
        // Check for watermark permission
        if (selectedElement.isWatermark && !can(Permission.EDIT_WATERMARKS)) {
          transformer.nodes([]);
        } else if (selectedNode.isListening() && selectedNode.getParent()) {
          transformer.nodes([selectedNode]);
        } else {
          transformer.nodes([]);
        }

        if (selectedElement.locked) {
          transformer.enabledAnchors([]);
          transformer.rotateEnabled(false);
          transformer.borderEnabled(true);
          transformer.borderStroke('#94a3b8');
          transformer.borderStrokeWidth(1);
          transformer.borderDash([4, 4]);
        } else {
          transformer.enabledAnchors(['top-left', 'top-center', 'top-right', 'middle-right', 'bottom-right', 'bottom-center', 'bottom-left', 'middle-left']);
          transformer.rotateEnabled(true);
          transformer.borderEnabled(true);
          transformer.borderStroke('#3b82f6');
          transformer.borderStrokeWidth(2);
          transformer.borderDash([]);
        }

        // Use the default bounding box function. The dummy rect defines the correct area.
        transformer.boundBoxFunc((_oldBox, newBox) => newBox);

      } else {
        transformer.nodes([]);
      }
      transformer.getLayer()?.batchDraw();
    };
    requestAnimationFrame(updateTransformer);
  }, [selectedElementId, elements, selectElement, stage, can]);
  return { transformerRef };
};